import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import DesignEditorLoadingIndicator from '../../../../components/global/DesignEditor/components/DesignEditorLoadingIndicator';
import DesignEditor from '../../../../components/global/DesignEditor';
import { ORIENTATION } from '../../../cards/constants';
import PrintProductGroupConfigurationItem from './components/PrintProductGroupConfigurationItem';
import PrintProductsGroupConfiguratorHeaderPane from './components/PrintProductsGroupConfiguratorHeaderPane';
import DesignEditorPreviewPane from '../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import Button from '../../../../components/global/Button';
import { getPhotoCardElements } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/components/AccountCardEmptyTextAlertModal/helpers';
import { getBackgroundAccountCardElement } from '../../../cards/views/AccountCardConfigurator/helpers';
import {
  isMultiIdentityPackageMembership,
  isMultiPosePackageMembership,
} from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/helpers';
import { getCardPhotoElementsWithConfigurationFromAccountCard } from './helpers';
import useRequestConfirm from '../../../../hooks/useRequestConfirm';

const propTypes = {
  className: PropTypes.string,
  printProducts: PropTypes.arrayOf(
    PropTypes.shape({
      printProductId: PropTypes.string,
      printProductSizeId: PropTypes.string,
      orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
      defaultAccountCard: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  buttonText: PropTypes.node,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  printBackgrounds: PropTypes.arrayOf({
    id: PropTypes.string,
    asset_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  allowPhotoChange: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
    watermarkMode: PropTypes.string,
    wallpaperWatermarkMode: PropTypes.string,
  }),
};

const PrintProductGroupConfigurator = ({
  className,
  photo,
  onComplete,
  albumMembershipGroup,
  identity,
  printProducts,
  printBackgrounds,
  onClose,
  watermarkSettings,
}) => {
  const requestConfirm = useRequestConfirm();
  const allowPhotoChange =
    isMultiPosePackageMembership(albumMembershipGroup) ?? true;
  const allowMultiIdentityPhoto =
    isMultiIdentityPackageMembership(albumMembershipGroup) ?? false;
  const [accountCardConfigurations, setAccountCardConfigurations] = useState(
    [],
  );
  const [activeItem, setActiveItem] = useState(0);
  const [printBackgroundModalOpen, setPrintBackgroundModalOpen] = useState(
    false,
  );
  const [
    printBackgroundModalHasOpened,
    setPrintBackgroundModalHasOpened,
  ] = useState(false);
  const [lastUsedPrintBackground, setLastUsedPrintBackground] = useState();
  const printProductItem = printProducts[activeItem];
  const { printProductSizeId } = printProductItem ?? {};
  const isLastPrintProductItem = activeItem + 1 === printProducts.length;

  const classes = classNames({
    [className]: !!className,
    'print-product-configurator': true,
    'print-product-group-configurator': true,
  });

  useEffect(() => {
    if (printBackgroundModalOpen) {
      setPrintBackgroundModalHasOpened(true);
    }
  }, [printBackgroundModalOpen]);

  if (!printProducts) {
    return <DesignEditorLoadingIndicator />;
  }

  const currentConfiguration = accountCardConfigurations.find(
    ({ id: printProductItemId, printProductSize }) => {
      if (printProductItemId) {
        return (
          printProductItemId === printProductItem?.id &&
          printProductSize.id === printProductSizeId
        );
      }

      return printProductSize.id === printProductSizeId;
    },
  );

  const {
    accountCard: activeAccountCard,
    accountCardElementReferences: activeAccountCardElementReferences,
  } = currentConfiguration ?? {};

  const isMissingPhotos =
    getPhotoCardElements({
      accountCard: activeAccountCard,
      emptyOnly: true,
    }).length !== 0;

  const backgroundElement = getBackgroundAccountCardElement({
    accountCard: activeAccountCard,
  });

  const photoElements = getCardPhotoElementsWithConfigurationFromAccountCard({
    accountCard: activeAccountCard,
  }).filter(
    elem =>
      elem?.cardElement?.id !== backgroundElement?.cardElement?.id &&
      (!elem.cardElementConfiguration ||
        elem.cardElementConfiguration?.is_editable),
  );

  const [mainPhotoElement] = photoElements ?? [];

  const photoElementReference =
    activeAccountCardElementReferences?.[mainPhotoElement?.cardElement?.id];

  const canCompleteSetup = !(
    (accountCardConfigurations?.length ?? 0) === 0 ||
    accountCardConfigurations.find(config => !config.accountCard) ||
    isMissingPhotos
  );
  // Add additional check for main photo element photo. THis basically allows
  // to show / remove the backdrop button if the main photo element is not
  // a GS photo.
  const hasPrintBackgrounds =
    !!backgroundElement &&
    !!mainPhotoElement?.cardElementConfiguration?.photo?.foreground &&
    printBackgrounds?.length > 0;

  const handleNextItem = () => {
    if (
      !printBackgroundModalHasOpened &&
      hasPrintBackgrounds &&
      printBackgrounds?.length > 1
    ) {
      requestConfirm({
        message: (
          <Fragment>
            <h4 className="text-center">Woah!</h4>
            <p className="text-center">
              Looks like you didn&apos;t update your print backdrop. This
              backdrop cannot be updated once submitted to print, so make sure
              everything looks exactly the way you want!
            </p>
          </Fragment>
        ),

        confirmText: "I'm happy with this!",
        cancelText: 'View available backdrops',
        cancelBtnStyle: 'primary',
        onConfirm: () => {
          if (!isLastPrintProductItem) {
            setActiveItem(activeItem + 1);
          } else {
            onComplete(accountCardConfigurations);
          }
          setPrintBackgroundModalHasOpened(true);
        },
        onCancel: () => {
          setPrintBackgroundModalHasOpened(true);
          setPrintBackgroundModalOpen(true);
        },
      });

      return;
    }

    if (!isLastPrintProductItem) {
      setActiveItem(activeItem + 1);
    } else {
      onComplete(accountCardConfigurations);
    }
  };

  const title = (() => {
    if (
      printProductItem?.printProductName &&
      printProductItem?.printProductSizeName
    ) {
      return `${printProductItem.printProductSizeName} ${pluralize(
        (printProductItem?.printProductName ?? '').replace(
          ' (configured as rendered sheets)',
          '',
        ),
        1,
      )}`;
    }

    return '';
  })();

  return (
    <DesignEditor className={classes}>
      <PrintProductsGroupConfiguratorHeaderPane
        onClose={onClose}
        activeItem={activeItem}
        printProductItem={printProductItem}
        totalAmount={printProducts.length}
      />

      <DesignEditorPreviewPane className="print-product-group-configurator-preview-pane">
        <h4 className="my-1 text-center">{title}</h4>
        <div className="print-product-group-actions-container">
          {photoElementReference && photoElements?.length === 1 && (
            <Fragment>
              {photoElementReference?.onPhotoChange && allowPhotoChange && (
                <Button
                  btnStyle="link"
                  btnSize="sm"
                  className="text-extra-bold"
                  onClick={() => {
                    photoElementReference.onPhotoChange();
                  }}
                >
                  Change Photo
                </Button>
              )}
              {photoElementReference?.onAdjustAndCrop && (
                <Button
                  btnStyle="link"
                  btnSize="sm"
                  className="text-extra-bold"
                  onClick={() => {
                    photoElementReference.onAdjustAndCrop();
                  }}
                >
                  Adjust &amp; Crop
                </Button>
              )}
            </Fragment>
          )}
          {hasPrintBackgrounds && (
            <Button
              btnStyle="link"
              btnSize="sm"
              className="text-extra-bold"
              onClick={() => setPrintBackgroundModalOpen(true)}
            >
              {backgroundElement?.cardElementConfiguration?.asset_image?.id
                ? 'Change Backdrop'
                : 'Add Backdrop'}
            </Button>
          )}
        </div>
        <PrintProductGroupConfigurationItem
          key={printProductItem?.id ?? printProductItem?.printProductSizeId}
          photo={photo}
          albumMembershipGroup={albumMembershipGroup}
          identity={identity}
          printBackgrounds={printBackgrounds}
          printBackgroundModalOpen={printBackgroundModalOpen}
          lastUsedPrintBackground={lastUsedPrintBackground}
          setLastUsedPrintBackground={setLastUsedPrintBackground}
          setPrintBackgroundModalOpen={setPrintBackgroundModalOpen}
          allowPhotoChange={allowPhotoChange}
          allowMultiIdentityPhoto={allowMultiIdentityPhoto}
          onAccountCardUpdated={({
            printProduct,
            printProductSize,
            accountCard,
            accountCardElementReferences,
          }) => {
            if (!printProductSize) {
              return;
            }

            const updatedConfig = {
              id: printProductItem?.id,
              printProduct,
              printProductSize,
              accountCard,
              accountCardElementReferences,
            };

            const activeConfiguration = accountCardConfigurations.find(
              config =>
                (config?.id && config.id === printProductItem?.id) ||
                (!config.id &&
                  config.printProductSize?.id === printProductSize?.id),
            );

            if (activeConfiguration) {
              setAccountCardConfigurations(current => [
                ...current.filter(
                  config =>
                    (config?.id && config.id !== printProductItem?.id) ||
                    (!config.id &&
                      config.printProductSize?.id !== printProductSize?.id),
                ),
                updatedConfig,
              ]);
            } else {
              setAccountCardConfigurations(current => [
                ...current,
                updatedConfig,
              ]);
            }
          }}
          watermarkSettings={watermarkSettings}
          {...printProductItem}
        />
        {canCompleteSetup && (
          <div className="print-product-group-configurator-continue-wrap">
            {canCompleteSetup && (
              <Button
                btnBlock
                btnSize="lg"
                onClick={handleNextItem}
                btnStyle="primary"
                data-testid="print-product-group-configurator-continue"
                className="print-product-group-configurator-continue"
              >
                I&apos;m happy,{' '}
                {isLastPrintProductItem
                  ? 'go to checkout'
                  : `let's configure the next product`}
                .
              </Button>
            )}
          </div>
        )}
      </DesignEditorPreviewPane>
    </DesignEditor>
  );
};

PrintProductGroupConfigurator.propTypes = propTypes;

export default PrintProductGroupConfigurator;
